// FIXME
// eslint-disable-next-line workspaces/require-dependency
import { loadContent } from '@databases/connections-api/load-content';
// eslint-disable-next-line workspaces/require-dependency
import { selectContentPropertyOptional } from '@databases/connections-api/selectors';
// eslint-disable-next-line workspaces/require-dependency
import { connectionsSlice, type FieldValueTypes } from '@databases/connections-api/slice';
// eslint-disable-next-line workspaces/require-dependency
import { type DefaultRootState } from '@databases/redux/default-root-state';
import { type ObjectMode, type Optional } from '@databases/types';
import {
  type BlogpostReference,
  type BlogpostReferenceOrContentId,
  type PageReference,
  type PageReferenceOrContentId,
} from '@databases/types/confluence';
import {
  type DatabaseField,
  DatabaseFieldType,
  type EntryWithContext,
  type FieldWithContext,
  type PageLabelsDelta,
  type RawDatabaseValue,
} from '@databases/types/database';
import { DatabaseFilterType } from '@databases/types/database/view';
import { isEqual } from 'lodash-es';

import { getConfluencePageAriFromContentRef } from '../../ariHelper';
import { addContentLabels, removeContentLabel } from '../../confluence/contentLabel';
import { getContentKey, getLabelPath } from '../../confluence/utils';
import { loadContentById } from '../../redux/content/actions';
import { selectContentRefOptional } from '../../redux/content/selectors';
import { selectBaseUrl } from '../../redux/context/selectors';
import { type SimpleStore } from '../../redux/redux';
import { encodeHtml } from '../../utils';
import type { FieldType } from '../field-types';
import {
  getConvertedDatabaseValue,
  getDatabaseEntryOptional,
  getDatabaseFieldOptional,
  isBrokenDatabase,
} from '../utils';

import {
  filterValidPageLinkValue,
  isExistingPageLinkValue,
  normalizePageLinkValue,
} from './pageLink';

/**
 * Returns the page link field in the same database that the page labels field references.
 */
export function getReferencedPageField({
  database,
  field,
}: FieldWithContext<DatabaseFieldType, ObjectMode>):
  | DatabaseField<DatabaseFieldType.PAGE_LINK>
  | undefined {
  const referencedField = getDatabaseFieldOptional(database, field.settings.fieldId);
  if (referencedField?.type !== DatabaseFieldType.PAGE_LINK) {
    return undefined;
  } else {
    return referencedField as DatabaseField<DatabaseFieldType.PAGE_LINK>;
  }
}

/**
 * Returns the content objects that the referenced page link field references and that are currently loaded into the Redux store.
 */
export function getReferencedLabelContentRefs<State extends DefaultRootState>(
  {
    database,
    field,
    entry,
  }: FieldWithContext<DatabaseFieldType, ObjectMode> &
    Optional<EntryWithContext<ObjectMode>, 'entry'>,
  state: State | undefined
): Array<PageReference | BlogpostReference> {
  if (!entry) {
    return [];
  }
  const referencedField = getReferencedPageField({ database, field });
  const referencedValue =
    referencedField && getConvertedDatabaseValue(database, referencedField, entry, state);
  return normalizePageLinkValue(referencedValue?.value ?? [], state).filter(
    (contentRef) => isExistingPageLinkValue(contentRef)
  );
}

/**
 * Returns the new labels for the content object if they are defined in the value, otherwise returns the existing labels of the content.
 */
export function getContentLabels(
  contentRef: PageReferenceOrContentId | BlogpostReferenceOrContentId,
  value: RawDatabaseValue<DatabaseFieldType.PAGE_LABELS>,
  state: DefaultRootState | undefined
): string[] | undefined {
  const ari = getConfluencePageAriFromContentRef(contentRef);
  if (!state) {
    return;
  }

  const pageLabels = selectContentPropertyOptional(
    state.connections,
    ari || '',
    'labels'
  )?.['data'] as Extract<FieldValueTypes, { type: 'tag' }>['value'][] | undefined;
  if (!pageLabels) {
    return;
  }

  return pageLabels.map((label) => label.text);
}

export async function applyPageLabels(
  contentRef: PageReference | BlogpostReference,
  delta: PageLabelsDelta,
  store: SimpleStore<DefaultRootState>,
  clearAll?: boolean
): Promise<void> {
  const resolvedContentRef = selectContentRefOptional(store.getState(), { contentRef });
  if (!resolvedContentRef) {
    return;
  }

  if (clearAll) {
    const ari = getConfluencePageAriFromContentRef(contentRef);
    if (ari) {
      store.dispatch(
        connectionsSlice.actions.removeColumnValueFromContent({
          ari,
          columnName: 'labels',
        })
      );
    }
  }

  await Promise.all([
    addContentLabels(resolvedContentRef.contentId, delta.add),
    ...delta.remove.map(async (label) => {
      await removeContentLabel(resolvedContentRef.contentId, label);
    }),
  ]);
  await store.dispatch(
    loadContentById(resolvedContentRef.contentId, { forceReload: true })
  );

  const ari = getConfluencePageAriFromContentRef(resolvedContentRef);
  if (ari) {
    await store.dispatch(loadContent({ ari, forceReload: true }));
  }
}

const pageLabelsFieldType: FieldType<DatabaseFieldType.PAGE_LABELS> = {
  supportedFilterTypes: [DatabaseFilterType.CONTAINS],

  isCsvImportSupported: false,

  renderExportView: ({ database, field, entry, value }, state) => {
    const baseUrl = selectBaseUrl(state);
    const contentRefs = getReferencedLabelContentRefs({ database, field, entry }, state);
    return contentRefs
      .map((contentRef) => {
        const labels = getContentLabels(contentRef, value, state);
        if (!labels || labels.length === 0) {
          return '&nbsp;';
        } else {
          return labels
            .map(
              (label) =>
                `<a href="${encodeHtml(
                  `${baseUrl}${getLabelPath(contentRef.spaceKey, label)}`
                )}" target="_blank">${encodeHtml(label)}</a>`
            )
            .join(' ');
        }
      })
      .join('<br/>');
  },

  parseFromHtml: (el, { field, entry, database }, state) => {
    const contentRefs = getReferencedLabelContentRefs({ database, field, entry }, state);
    return Object.fromEntries(
      contentRefs.map((contentRef) => {
        const existingLabels = getContentLabels(contentRef, {}, state);
        return existingLabels ? [getContentKey(contentRef), existingLabels] : [];
      })
    );
  },

  getInitialValue: () => ({}),

  isEmptyValue: ({ database, field, entry, value }, state) => {
    const allLabels = getReferencedLabelContentRefs({ database, field, entry }, state)
      .flatMap((contentRef) => getContentLabels(contentRef, value, state))
      .filter((value) => value !== undefined);
    return allLabels.length === 0;
  },

  isEqualValue: (context1, context2, state) => {
    const contentRefs1 = getReferencedLabelContentRefs(context1, state);
    const contentRefs2 = getReferencedLabelContentRefs(context2, state);
    if (
      !isEqual(
        contentRefs1.map((contentRef) => getContentKey(contentRef)),
        contentRefs2.map((contentRef) => getContentKey(contentRef))
      )
    ) {
      return false;
    }
    return contentRefs1.every((contentRef, idx) =>
      isEqual(
        getContentLabels(contentRef, context1.value, state),
        getContentLabels(contentRefs2[idx], context2.value, state)
      )
    );
  },

  getStringRepresentation: ({ database, field, entry, value }, state) => {
    if (!state) {
      return '';
    }

    return getReferencedLabelContentRefs({ database, field, entry }, state)
      .map((obj) => (getContentLabels(obj, value, state) ?? []).join(' '))
      .join('\n');
  },

  loadDependencies: (
    { contentId, field, entryId },
    { dispatch, selector, selectDatabase }
  ) => {
    const referencedField = selector((state) => {
      const _db = selectDatabase(state, { contentId });
      const database = _db && !isBrokenDatabase(_db) ? _db : undefined;
      return database && getReferencedPageField({ database, field });
    });

    const referencedValue = selector((state) => {
      const _db = selectDatabase(state, { contentId });
      const database = _db && !isBrokenDatabase(_db) ? _db : undefined;
      const entry =
        database && entryId ? getDatabaseEntryOptional(database, entryId) : undefined;
      return (
        database &&
        referencedField &&
        entry &&
        getConvertedDatabaseValue(database, referencedField, entry, state)
      );
    });

    if (referencedValue) {
      for (const contentRef of filterValidPageLinkValue(referencedValue.value)) {
        if (!isExistingPageLinkValue(contentRef) || !contentRef.contentId) {
          continue;
        }

        dispatch(loadContentById(contentRef.contentId));

        const ari = getConfluencePageAriFromContentRef(contentRef);
        if (ari && referencedField) {
          dispatch(
            loadContent({
              ari,
              connectionId: referencedField.id,
            })
          );
        }
      }
    }
  },

  noDefaultValue: true,
};

export default pageLabelsFieldType;
